/**
 * Isomorphic helpers shared by api, web, and admin.
 * Nothing here may touch Node-only or browser-only globals.
 */

export function slugify(value) {
  return String(value)
    .normalize('NFKD')
    // strip combining diacritics left behind by NFKD
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function formatDate(value, locale = 'en-US') {
  if (!value) return '';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(value instanceof Date ? value : new Date(value));
}

export function formatNumber(value, locale = 'en-US') {
  return new Intl.NumberFormat(locale).format(Number(value) || 0);
}

/** 1234 → "1.2k". Used across analytics tiles. */
export function compactNumber(value, locale = 'en-US') {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 })
    .format(Number(value) || 0);
}

export function formatDuration(ms) {
  const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function formatBytes(bytes) {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Number(bytes) || 0;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function percent(part, total, digits = 1) {
  const t = Number(total);
  if (!t) return '0%';
  return `${((Number(part) / t) * 100).toFixed(digits)}%`;
}

export function clamp(value, min, max) {
  return Math.min(Math.max(Number(value), min), max);
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value ?? '').trim());
}

/** Strips control characters and trims; the first line of defence on any input. */
export function sanitizeText(value, maxLength = 5000) {
  return String(value ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F]/g, '')
    .trim()
    .slice(0, maxLength);
}

export function safeJsonParse(value, fallback = null) {
  try {
    return typeof value === 'string' ? JSON.parse(value) : (value ?? fallback);
  } catch {
    return fallback;
  }
}

export function debounce(fn, wait = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

export function throttle(fn, limit = 100) {
  let waiting = false;
  let trailing = null;
  return (...args) => {
    if (waiting) {
      trailing = args;
      return;
    }
    fn(...args);
    waiting = true;
    setTimeout(() => {
      waiting = false;
      if (trailing) {
        fn(...trailing);
        trailing = null;
      }
    }, limit);
  };
}

export function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function unique(items) {
  return Array.from(new Set(items));
}

export function groupBy(items, keyFn) {
  return items.reduce((acc, item) => {
    const key = keyFn(item);
    (acc[key] ||= []).push(item);
    return acc;
  }, {});
}

/** Cycles a palette so sequential cards alternate accents. */
export function pickAccent(index, palette) {
  return palette[index % palette.length];
}

/** Deterministic small rotation, so "random" sticker tilt is stable across SSR and hydration. */
export function stableRotation(seed, maxDegrees = 3) {
  let hash = 0;
  const str = String(seed);
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return ((Math.abs(hash) % (maxDegrees * 200)) / 100 - maxDegrees).toFixed(2);
}

export function normalizePath(path = '/') {
  const clean = String(path).split('?')[0].split('#')[0];
  if (clean === '/') return '/';
  return clean.replace(/\/+$/, '') || '/';
}

export function parseUtm(query = {}) {
  const pick = (key) => {
    const value = query[key];
    return value ? sanitizeText(value, 255) : null;
  };
  return {
    utmSource: pick('utm_source'),
    utmMedium: pick('utm_medium'),
    utmCampaign: pick('utm_campaign'),
    utmTerm: pick('utm_term'),
    utmContent: pick('utm_content'),
  };
}

export function referrerDomain(referrer) {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
